import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import PlanModal from './PlanModal';
import PlanMetaModal from './PlanMetaModal';
import { Button } from 'react-bootstrap';

function PlanManagement() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [metaModalShow, setMetaModalShow] = useState(false);
  const [metaEditing, setMetaEditing] = useState(null);
  const [metaPlanId, setMetaPlanId] = useState(null);
  const [planMeta, setPlanMeta] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [frameworkOptions, setFrameworkOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);

  const fetchPlans = () => {
    setLoading(true);
    api.get('/plans')
      .then(res => setPlans(res.data.plans || []))
      .catch(() => setError('Failed to load plans'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPlans();
    api.get('/frameworks').then(res => setFrameworkOptions(res.data.frameworks || []));
    api.get('/channels').then(res => setChannelOptions(res.data.channels || []));
  }, []);

  const handleEdit = plan => {
    setEditingPlan(plan);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingPlan(null);
    setShowModal(true);
  };

  const handleDelete = async plan => {
    if (!window.confirm(`Delete plan "${plan.name}"?`)) return;
    setSaving(true);
    try {
      await api.delete(`/plans/${plan.id}`);
      fetchPlans();
    } catch {
      setError('Failed to delete plan');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (planData) => {
    setSaving(true);
    try {
      if (editingPlan) {
        await api.put(`/plans/${editingPlan.id}`, planData);
      } else {
        await api.post('/plans', planData);
      }
      setShowModal(false);
      fetchPlans();
    } catch {
      setError('Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  // Fetch plan meta for a plan
  const fetchPlanMeta = async (planId) => {
    setMetaLoading(true);
    setMetaPlanId(planId);
    setMetaEditing(null);
    try {
      const res = await api.get(`/plans/${planId}/meta`);
      setPlanMeta(res.data.meta || []);
    } catch {
      setPlanMeta([]);
    } finally {
      setMetaLoading(false);
    }
  };

  // Open meta modal for add/edit
  const handleAddMeta = (planId) => {
    setMetaPlanId(planId);
    setMetaEditing(null);
    setMetaModalShow(true);
  };
  const handleEditMeta = (meta) => {
    setMetaEditing(meta);
    setMetaModalShow(true);
  };
  const handleDeleteMeta = async (metaId) => {
    if (!window.confirm('Delete this meta?')) return;
    await api.delete(`/plans/${metaPlanId}/meta/${metaId}`);
    fetchPlanMeta(metaPlanId);
  };
  const handleSaveMeta = async (meta) => {
    if (metaEditing) {
      await api.put(`/plans/${metaPlanId}/meta/${metaEditing.id}`, meta);
    } else {
      await api.post(`/plans/${metaPlanId}/meta`, meta);
    }
    setMetaModalShow(false);
    fetchPlanMeta(metaPlanId);
  };

  if (loading) return <div>Loading plans...</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Plan Management</h2>
        <Button variant="success" onClick={handleCreate}>Create Plan</Button>
      </div>
      <table className="table table-striped table-hover">
        <thead>
          <tr>
            <th>Name</th>
            <th>Price</th>
            <th>Duration</th>
            <th>Features</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {plans.map((plan, i) => {
            // Parse features/meta for display
            let features = {};
            if (plan.plan_meta) {
              for (const meta of plan.plan_meta) {
                if (meta.meta_key === 'free_trial') features.free_trial = meta.meta_value === 'true' || meta.meta_value === true;
                if (meta.meta_key === 'free_trial_days') features.free_trial_days = Number(meta.meta_value) || '';
                if (meta.meta_key === 'frameworks') features.frameworks = Array.isArray(meta.meta_value) ? meta.meta_value : (meta.meta_value ? JSON.parse(meta.meta_value) : []);
                if (meta.meta_key === 'channels') features.channels = Array.isArray(meta.meta_value) ? meta.meta_value : (meta.meta_value ? JSON.parse(meta.meta_value) : []);
              }
            } else if (plan.features) {
              try { features = typeof plan.features === 'string' ? JSON.parse(plan.features) : plan.features; } catch { features = {}; }
            }
            // Map framework/channel IDs to names
            const frameworkNames = (features.frameworks || []).map(fid => {
              const f = frameworkOptions.find(fr => fr.id === Number(fid) || fr.id === fid);
              return f ? f.name : fid;
            });
            const channelNames = (features.channels || []).map(cid => {
              const c = channelOptions.find(ch => ch.id === Number(cid) || ch.id === cid);
              return c ? c.name : cid;
            });
            // Build readable features string
            const featureStrings = [];
            if (features.free_trial) featureStrings.push(`Free Trial (${features.free_trial_days || ''} days)`);
            if (frameworkNames.length) featureStrings.push(`Frameworks: ${frameworkNames.join(', ')}`);
            if (channelNames.length) featureStrings.push(`Channels: ${channelNames.join(', ')}`);
            return (
              <tr key={i}>
                <td>{plan.name}</td>
                <td>${plan.price}</td>
                <td>{plan.duration} days</td>
                <td>{featureStrings.length ? featureStrings.join(' | ') : '—'}</td>
                <td>{plan.is_active ? 'Active' : 'Inactive'}</td>
                <td>
                  <Button size="sm" variant="primary" className="me-2" onClick={() => handleEdit(plan)}>Edit</Button>
                  <Button size="sm" variant="danger" disabled={saving} onClick={() => handleDelete(plan)}>Delete</Button>
                  <Button size="sm" variant="info" className="ms-2" onClick={() => { fetchPlanMeta(plan.id); setMetaModalShow(false); }}>
                    Meta
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <PlanModal show={showModal} onHide={() => setShowModal(false)} onSave={handleSave} plan={editingPlan} />

      {/* Plan Meta Table & Modal */}
      {metaPlanId && (
        <div className="mt-4">
          <h5>Plan Meta</h5>
          {metaLoading ? <div>Loading...</div> : (
            <>
              <Button size="sm" variant="success" className="mb-2" onClick={() => handleAddMeta(metaPlanId)}>Add Meta</Button>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Value</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {planMeta.length === 0 && <tr><td colSpan="3">No meta</td></tr>}
                  {planMeta.map(meta => (
                    <tr key={meta.id}>
                      <td>{meta.meta_key}</td>
                      <td>{meta.meta_value}</td>
                      <td>
                        <Button size="sm" variant="primary" className="me-2" onClick={() => handleEditMeta(meta)}>Edit</Button>
                        <Button size="sm" variant="danger" onClick={() => handleDeleteMeta(meta.id)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
      <PlanMetaModal show={metaModalShow} onHide={() => setMetaModalShow(false)} onSave={handleSaveMeta} meta={metaEditing} isEditing={!!metaEditing} />
    </div>
  );
}

export default PlanManagement;
